import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { PipeInventoryDialogComponent } from './pipe-inventory-dialog.component';
import { StockMovementDialogComponent } from './stock-movement-dialog.component';
import { PurchaseOrderDialogComponent } from './purchase-order-dialog.component';
import { environment } from 'src/environments/environment';

// Temporary interfaces until service is properly integrated
export interface PipeInventory {
  pipe_id: number;
  pipe_tubo_type: string;
  qty_on_stock: number;
  area_code: number;
  area_name: string;
  exp_category_id: number;
  exp_category_name: string;
  zone: number;
  unit_cost: number;
  minimum_stock: number;
  maximum_stock: number;
  reorder_point: number;
  last_updated: string;
  notes: string;
  status: string;
  total_value: number;
  stock_status: string;
}

export interface InventoryCategory {
  category_id: number;
  category_name: string;
  category_code: string;
  description: string;
  depreciation_method: string;
  useful_life_years: number;
  salvage_value_percentage: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Area {
  area_id: number;
  area_name: string;
  area_code: string;
  zone: number;
  status: string;
}

@Component({
  selector: 'app-inventory-management',
  templateUrl: './inventory-management.component.html',
  styleUrls: ['./inventory-management.component.scss']
})
export class InventoryManagementComponent implements OnInit {
  displayedColumns: string[] = [
    'pipe_id', 'pipe_tubo_type', 'qty_on_stock', 'area_name', 
    'zone', 'unit_cost', 'total_value', 'stock_status', 'status', 'actions'
  ];
  
  dataSource = new MatTableDataSource<PipeInventory>();
  pipes: PipeInventory[] = [];
  categories: InventoryCategory[] = [];
  areas: Area[] = [];
  loading = false;
  totalItems = 0;
  totalValue = 0;
  lowStockItems = 0;
  outOfStockItems = 0;

  // Filters
  filterStatus = '';
  filterArea = '';
  filterZone = '';
  searchQuery = '';

  constructor(
    private http: HttpClient,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadPipeInventory();
    this.loadCategories();
    this.loadAreas();
  }

  loadPipeInventory(): void {
    this.loading = true;
    this.http.get<PipeInventory[]>(`${environment.apiServiceBaseUrl}/hawdco/pipe-inventory`).subscribe({
      next: (data) => {
        this.pipes = data;
        this.dataSource.data = data;
        this.calculateTotals();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading pipe inventory:', error);
        this.loading = false;
      }
    });
  }

  loadCategories(): void {
    this.http.get<InventoryCategory[]>(`${environment.apiServiceBaseUrl}/hawdco/inventory-categories`).subscribe({
      next: (data) => {
        this.categories = data;
      },
      error: (error) => {
        console.error('Error loading categories:', error);
      }
    });
  }

  loadAreas(): void {
    this.http.get<Area[]>(`${environment.apiServiceBaseUrl}/hawdco/areas`).subscribe({
      next: (data) => {
        this.areas = data;
      },
      error: (error) => {
        console.error('Error loading areas:', error);
      }
    });
  }

  calculateTotals(): void {
    this.totalItems = this.pipes.length;
    this.totalValue = this.pipes.reduce((sum, pipe) => sum + (pipe.qty_on_stock * pipe.unit_cost), 0);
    this.lowStockItems = this.pipes.filter(pipe => pipe.stock_status === 'Low Stock').length;
    this.outOfStockItems = this.pipes.filter(pipe => pipe.stock_status === 'Out of Stock').length;
  }

  applyFilter(): void {
    let filteredPipes = this.pipes;

    if (this.filterStatus) {
      filteredPipes = filteredPipes.filter(pipe => pipe.status === this.filterStatus);
    }

    if (this.filterArea) {
      filteredPipes = filteredPipes.filter(pipe => pipe.area_name === this.filterArea);
    }

    if (this.filterZone) {
      filteredPipes = filteredPipes.filter(pipe => pipe.zone.toString() === this.filterZone);
    }

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filteredPipes = filteredPipes.filter(pipe => 
        pipe.pipe_tubo_type.toLowerCase().includes(query) ||
        pipe.notes?.toLowerCase().includes(query)
      );
    }

    this.dataSource.data = filteredPipes;
  }

  openPipeDialog(pipe?: PipeInventory): void {
    const dialogRef = this.dialog.open(PipeInventoryDialogComponent, {
      width: '800px',
      data: pipe || {}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (pipe) {
          this.updatePipe(result);
        } else {
          this.createPipe(result);
        }
      }
    });
  }

  createPipe(pipe: any): void {
    this.http.post(`${environment.apiServiceBaseUrl}/hawdco/pipe-inventory`, pipe).subscribe({
      next: () => {
        this.loadPipeInventory();
      },
      error: (error) => {
        console.error('Error creating pipe:', error);
      }
    });
  }

  updatePipe(pipe: any): void {
    this.http.put(`${environment.apiServiceBaseUrl}/hawdco/pipe-inventory/${pipe.pipe_id}`, pipe).subscribe({
      next: () => {
        this.loadPipeInventory();
      },
      error: (error) => {
        console.error('Error updating pipe:', error);
      }
    });
  }

  deletePipe(pipeId: number): void {
    if (confirm('Are you sure you want to delete this pipe inventory item?')) {
      this.http.delete(`${environment.apiServiceBaseUrl}/hawdco/pipe-inventory/${pipeId}`).subscribe({
        next: () => {
          this.loadPipeInventory();
        },
        error: (error) => {
          console.error('Error deleting pipe:', error);
        }
      });
    }
  }

  viewStockMovements(pipe: PipeInventory): void {
    const dialogRef = this.dialog.open(StockMovementDialogComponent, {
      width: '900px',
      data: pipe
    });
  }

  addStockMovement(pipe: PipeInventory): void {
    const dialogRef = this.dialog.open(StockMovementDialogComponent, {
      width: '800px',
      data: { pipe: pipe, isNew: true }
    });
  }

  createPurchaseOrder(): void {
    const dialogRef = this.dialog.open(PurchaseOrderDialogComponent, {
      width: '1000px',
      data: { pipes: this.pipes }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadPipeInventory();
      }
    });
  }

  exportToExcel(): void {
    this.http.get(`${environment.apiServiceBaseUrl}/hawdco/pipe-inventory/export`, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'pipe-inventory.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error exporting inventory:', error);
      }
    });
  }

  generateStockReport(): void {
    this.http.get(`${environment.apiServiceBaseUrl}/hawdco/pipe-inventory/stock-report`, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'stock-report.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error generating stock report:', error);
      }
    });
  }

  getStockStatusColor(status: string): string {
    switch (status) {
      case 'In Stock': return 'primary';
      case 'Low Stock': return 'accent';
      case 'Out of Stock': return 'warn';
      default: return 'basic';
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'active': return 'primary';
      case 'inactive': return 'accent';
      case 'discontinued': return 'warn';
      default: return 'basic';
    }
  }

  getUniqueZones(): number[] {
    return [...new Set(this.pipes.map(pipe => pipe.zone))].sort();
  }
}
